import React, { useEffect, useState, useMemo } from "react";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";
import Avatar from "../components/Avatar";
import AttendanceMapDialog from "../components/AttendanceMapDialog";
import { MapPin, Search, ListChecks, X, Check, Utensils, Coffee, LogIn, LogOut, Calendar, Download, ChevronDown } from "lucide-react";
import WorkModeBadge from "../components/WorkModeBadge";

const KIND_LABEL = { in: "Entrata", out: "Uscita", lunch_start: "Pausa pranzo", lunch_end: "Fine pausa" };
const KIND_COLOR = { in: "bg-emerald-100 text-emerald-700 border-emerald-200", out: "bg-blue-100 text-blue-700 border-blue-200", lunch_start: "bg-amber-100 text-amber-700 border-amber-200", lunch_end: "bg-amber-50 text-amber-600 border-amber-200" };
const KIND_ICON = { in: LogIn, out: LogOut, lunch_start: Utensils, lunch_end: Coffee };

function fmtDT(ts) { try { return new Date(ts).toLocaleString("it-IT", { dateStyle: "short", timeStyle: "short" }); } catch { return ""; } }
function todayIso() { return new Date().toISOString().slice(0, 10); }
function daysAgo(n) { const d = new Date(); d.setDate(d.getDate() - n); return d.toISOString().slice(0, 10); }

export default function AttendanceLogPage() {
  const [rows, setRows] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [filters, setFilters] = useState({
    employee_id: "all",
    date_from: daysAgo(7),
    date_to: todayIso(),
    valid: "all",
    work_mode: "all",
  });
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.employee_id && filters.employee_id !== "all") params.append("employee_id", filters.employee_id);
      if (filters.date_from) params.append("date_from", filters.date_from);
      if (filters.date_to) params.append("date_to", filters.date_to);
      if (filters.valid && filters.valid !== "all") params.append("valid", filters.valid);
      params.append("limit", "500");
      const r = await api.get(`/attendances/log?${params.toString()}`);
      setRows(r.data || []);
    } catch { setRows([]); }
    finally { setLoading(false); }
  };

  useEffect(() => { api.get("/employees").then(r => setEmployees(r.data || [])).catch(()=>{}); }, []);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filters]);

  const filtered = useMemo(() => {
    let out = rows;
    if (filters.work_mode && filters.work_mode !== "all") {
      out = out.filter(r => {
        const m = r.work_mode || (r.valid ? "office" : null);
        return m === filters.work_mode;
      });
    }
    if (!search.trim()) return out;
    const s = search.toLowerCase();
    return out.filter(r =>
      (r.user_full_name || "").toLowerCase().includes(s) ||
      (r.qr_label || "").toLowerCase().includes(s) ||
      (r.kind || "").toLowerCase().includes(s) ||
      (r.note || "").toLowerCase().includes(s)
    );
  }, [rows, search, filters.work_mode]);

  // Group by date
  const byDate = useMemo(() => {
    const m = {};
    for (const r of filtered) {
      const d = (r.timestamp || "").slice(0, 10);
      if (!m[d]) m[d] = [];
      m[d].push(r);
    }
    return Object.entries(m).sort((a, b) => b[0].localeCompare(a[0]));
  }, [filtered]);

  const exportCSV = () => {
    const headers = ["data", "ora", "dipendente", "tipo", "qr_label", "modalita", "nota", "valida", "distanza_m", "latitudine", "longitudine"];
    const lines = [headers.join(";")];
    const wmLabel = { office: "Sede", smartworking: "Smart working", trasferta: "Trasferta" };
    for (const r of filtered) {
      const dt = new Date(r.timestamp);
      lines.push([
        dt.toLocaleDateString("it-IT"),
        dt.toLocaleTimeString("it-IT"),
        `"${r.user_full_name || ""}"`,
        KIND_LABEL[r.kind] || r.kind,
        `"${r.qr_label || ""}"`,
        wmLabel[r.work_mode] || (r.valid ? "Sede" : ""),
        `"${(r.note || "").replace(/"/g, '""')}"`,
        r.valid ? "SI" : "NO",
        r.distance_m != null ? Math.round(r.distance_m) : "",
        r.latitude,
        r.longitude,
      ].join(";"));
    }
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `timbrature_${filters.date_from}_${filters.date_to}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const totals = useMemo(() => ({
    total: filtered.length,
    valid: filtered.filter(r => r.valid).length,
    invalid: filtered.filter(r => !r.valid).length,
    employees: new Set(filtered.map(r => r.user_id)).size,
  }), [filtered]);

  return (
    <div className="p-4 md:p-12" data-testid="attendance-log-page">
      <div className="flex items-start justify-between mb-6 flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-widest text-[#757575] mb-2 flex items-center gap-2">
            <ListChecks className="w-3 h-3"/> Registro Timbrature
          </div>
          <h1 className="font-display text-4xl">Listato giornaliero</h1>
          <p className="text-[#555] mt-1 text-sm">Tutte le timbrature aziendali · filtra per dipendente, data, validità · clicca una riga per vedere la posizione</p>
        </div>
        <Button onClick={exportCSV} variant="outline" className="rounded-full h-10 gap-2" data-testid="attlog-export-csv">
          <Download className="w-4 h-4"/> Esporta CSV
        </Button>
      </div>

      {/* Filtri */}
      <div className="card-smart p-4 mb-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-3">
        <div className="lg:col-span-2">
          <Label className="text-xs">Dipendente</Label>
          <Select value={filters.employee_id} onValueChange={(v) => setFilters({ ...filters, employee_id: v })}>
            <SelectTrigger className="rounded-xl mt-1" data-testid="attlog-employee-filter"><SelectValue/></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutti i dipendenti</SelectItem>
              {employees.map(e => <SelectItem key={e.id} value={e.id}>{e.name} {e.lastname}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs">Dal</Label>
          <Input type="date" className="rounded-xl mt-1" value={filters.date_from} onChange={e => setFilters({ ...filters, date_from: e.target.value })} data-testid="attlog-date-from"/>
        </div>
        <div>
          <Label className="text-xs">Al</Label>
          <Input type="date" className="rounded-xl mt-1" value={filters.date_to} onChange={e => setFilters({ ...filters, date_to: e.target.value })} data-testid="attlog-date-to"/>
        </div>
        <div>
          <Label className="text-xs">Stato GPS</Label>
          <Select value={filters.valid} onValueChange={(v) => setFilters({ ...filters, valid: v })}>
            <SelectTrigger className="rounded-xl mt-1" data-testid="attlog-valid-filter"><SelectValue/></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutte</SelectItem>
              <SelectItem value="valid">Solo valide</SelectItem>
              <SelectItem value="invalid">Solo fuori area</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs">Modalità</Label>
          <Select value={filters.work_mode} onValueChange={(v) => setFilters({ ...filters, work_mode: v })}>
            <SelectTrigger className="rounded-xl mt-1" data-testid="attlog-mode-filter"><SelectValue/></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutte</SelectItem>
              <SelectItem value="office">Solo Sede</SelectItem>
              <SelectItem value="smartworking">Solo Smart working</SelectItem>
              <SelectItem value="trasferta">Solo Trasferta</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Search bar + stats */}
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div className="relative flex-1 max-w-md">
          <Search className="w-4 h-4 absolute left-3 top-3 text-[#999]"/>
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Cerca per nome dipendente, sede QR…"
            className="pl-9 rounded-full"
            data-testid="attlog-search"
          />
        </div>
        <div className="flex gap-2 text-xs">
          <span className="px-3 py-1.5 rounded-full bg-[#1E2347] text-[#7C3AED] font-semibold" data-testid="attlog-total">{totals.total} timbrature</span>
          <span className="px-3 py-1.5 rounded-full bg-emerald-50 text-emerald-700 font-semibold">{totals.valid} OK</span>
          {totals.invalid > 0 && <span className="px-3 py-1.5 rounded-full bg-red-50 text-red-700 font-semibold">{totals.invalid} fuori area</span>}
          <span className="px-3 py-1.5 rounded-full bg-blue-50 text-blue-700 font-semibold">{totals.employees} dipendenti</span>
        </div>
      </div>

      {/* List grouped by date */}
      {loading && <div className="text-center py-8 text-sm text-[#757575]">Caricamento…</div>}
      {!loading && byDate.length === 0 && (
        <div className="card-smart p-12 text-center">
          <ListChecks className="w-12 h-12 mx-auto mb-3 text-[#3B82F6]/30"/>
          <div className="text-sm text-[#757575]">Nessuna timbratura nel periodo selezionato</div>
        </div>
      )}
      <div className="space-y-4">
        {byDate.map(([date, atts]) => (
          <div key={date} className="card-smart overflow-hidden" data-testid={`attlog-day-${date}`}>
            <div className="bg-gradient-to-r from-[#0A0A0A] to-neutral-800 text-white px-4 py-2.5 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-[#3B82F6]"/>
                <span className="font-display text-base">{new Date(date).toLocaleDateString("it-IT", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}</span>
              </div>
              <span className="text-xs text-white/60">{atts.length} timbrature</span>
            </div>
            <div className="divide-y divide-[#1F2540]">
              {atts.map((a) => {
                const Icon = KIND_ICON[a.kind] || Calendar;
                const cls = KIND_COLOR[a.kind] || "bg-neutral-100 text-neutral-700 border-neutral-200";
                return (
                  <button
                    key={a.id}
                    onClick={() => setSelected(a)}
                    data-testid={`attlog-row-${a.id}`}
                    className="w-full text-left px-4 py-3 hover:bg-[#1E2347]/50 transition flex items-center gap-3"
                  >
                    <Avatar avatar={a.user_avatar} name={a.user_full_name?.split(" ")[0]} lastname={a.user_full_name?.split(" ").slice(1).join(" ")} size={36}/>
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-sm truncate flex items-center gap-2 flex-wrap">
                        <span>{a.user_full_name}</span>
                        <WorkModeBadge mode={a.work_mode}/>
                      </div>
                      <div className="text-xs text-[#757575] truncate flex items-center gap-1">
                        <MapPin className="w-3 h-3"/> {a.qr_label || "—"}
                      </div>
                      {a.note && <div className="text-xs text-[#444] italic truncate mt-0.5" title={a.note}>“{a.note}”</div>}
                    </div>
                    <span className={`px-2 py-1 rounded-full text-xs border flex items-center gap-1 ${cls}`}>
                      <Icon className="w-3 h-3"/>
                      {KIND_LABEL[a.kind] || a.kind}
                    </span>
                    <div className="text-right text-xs min-w-[64px]">
                      <div className="font-semibold text-sm">{new Date(a.timestamp).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" })}</div>
                      {a.distance_m != null && <div className="text-[#757575]">{Math.round(a.distance_m)}m</div>}
                    </div>
                    {a.valid ? (
                      <Check className="w-5 h-5 text-emerald-600 flex-shrink-0" title="Posizione valida"/>
                    ) : (
                      <X className="w-5 h-5 text-red-600 flex-shrink-0" title="Fuori area"/>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      {selected && <AttendanceMapDialog att={selected} onClose={() => setSelected(null)}/>}
    </div>
  );
}
